import type { SavedProperty } from "../../../lib/properties";
import type { DashboardSnapshot } from "../../../lib/dashboard";

export function resolvePropertyFit(
  property: SavedProperty,
  snapshot: DashboardSnapshot,
  monthlyPayment: number,
  planUnderfunded = false,
) {
  // SILL-318: when the selected plan is not executable (liquid assets do
  // not cover cash to close + reserves), suppress monthly-comfort copy
  // at the top of the card. The monthly-comfort delta still appears as
  // sub-line copy elsewhere — see estimate-snapshot.ts. Returning early
  // here keeps the inferred-fit ladder untouched for executable plans.
  if (planUnderfunded) {
    return {
      className: "text-destructive",
      description:
        "Liquid assets do not yet cover the down payment, closing costs, and reserves this plan would need.",
      label: "Plan underfunded",
    };
  }

  const inferred =
    property.affordability_fit ??
    (snapshot.monthlyBudget > 0 && monthlyPayment <= snapshot.monthlyBudget
      ? "comfortable"
      : property.list_price <= snapshot.buyingPowerMax
        ? "stretch"
        : "out_of_range");

  if (inferred === "comfortable") {
    return {
      className: "text-primary-dark",
      description:
        "Payment sits inside the current comfort target, before final taxes, insurance, HOA, and lender review.",
      label: "Within comfort",
    };
  }

  if (inferred === "stretch") {
    return {
      className: "text-warning-foreground",
      description:
        "This target is worth comparing, but the monthly or cash buffer is thin.",
      label: "Stretch",
    };
  }

  return {
    className: "text-destructive",
    description:
      "Readiness can still be healthy while this home's payment or cash need sits outside current comfort.",
    label: "Outside comfort",
  };
}
